import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Chip, Paper, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton,
  Select, FormControl, InputLabel, CircularProgress, Alert, Tabs, Tab,
  LinearProgress
} from '@mui/material';
import {
  Schedule as ScheduleIcon, Add, PlayArrow, Pause, PlayCircleOutline,
  Delete, Visibility, ChevronLeft, ChevronRight, Today, History, FolderOpen, Code
} from '@mui/icons-material';
import api from '../../services/api';

const WEEKDAY_LABELS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const MONTH_LABELS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const STATUS_META = {
  running: { label: 'En progreso', color: 'warning' },
  completed: { label: 'Completada', color: 'success' },
  failed: { label: 'Fallida', color: 'error' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CL', { dateStyle: 'medium', timeStyle: 'short' });
}
function fmtDuration(sec) {
  if (sec == null) return '—';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  return `${m}m ${sec % 60}s`;
}
function devStateMeta(taskOrRun) {
  if (!taskOrRun) return null;
  if (taskOrRun.dev_completed) return { label: 'Completada 100%', color: 'success' };
  if (taskOrRun.requires_dev) return { label: 'Incompleta · Requiere Desarrollo', color: 'warning' };
  return null;
}

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function occurrencesInMonth(task, year, month) {
  const out = [];
  if (!task.next_run_at && task.status !== 'active') return out;
  const first = new Date(year, month, 1, 0, 0, 0);
  const last = new Date(year, month + 1, 0, 23, 59, 59);
  const nextRun = task.next_run_at ? new Date(task.next_run_at) : null;
  const [hh, mm] = (task.run_time || '09:00').split(':').map(Number);

  const pushDay = (d) => {
    const occ = new Date(d.getFullYear(), d.getMonth(), d.getDate(), hh || 0, mm || 0);
    if (occ >= first && occ <= last) {
      if (!nextRun || occ >= new Date(nextRun.getTime() - 60000)) out.push({ date: occ, task });
    }
  };

  if (task.schedule_type === 'once') {
    if (nextRun && nextRun >= first && nextRun <= last) out.push({ date: nextRun, task });
  } else if (task.schedule_type === 'daily') {
    for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) pushDay(d);
  } else if (task.schedule_type === 'weekly') {
    const targetWd = task.weekday != null ? task.weekday : 0;
    for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
      if ((d.getDay() + 6) % 7 === targetWd) pushDay(d);
    }
  }
  return out;
}

// ---------- Diálogo de detalle de ejecución ----------
function RunDetailDialog({ runId, open, onClose }) {
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState(0);
  const logRef = useRef(null);

  useEffect(() => {
    if (!open || !runId) return;
    let alive = true;
    setDetail(null);
    setTab(0);
    const load = async () => {
      try {
        const res = await api.get(`/tareas-programadas/runs/${runId}?include_files=true`);
        if (alive) setDetail(res.data);
        return res.data?.status === 'running';
      } catch (err) { return false; }
    };
    load().then((stillRunning) => {
      if (stillRunning) setTab(0);
    });
    const iv = setInterval(async () => {
      const still = await load();
      if (!alive) return;
    }, 2500);
    return () => { alive = false; clearInterval(iv); };
  }, [open, runId]);

  useEffect(() => {
    if (detail?.status === 'running' && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [detail]);

  if (!runId) return null;
  const isRunning = detail?.status === 'running';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Ejecución — {detail?.criterion_title || '…'}
        {detail && (
          <Chip size="small" sx={{ ml: 1 }} label={STATUS_META[detail.status]?.label || detail.status}
            color={STATUS_META[detail.status]?.color || 'default'} />
        )}
      </DialogTitle>
      <DialogContent>
        {!detail ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={3}><Typography variant="caption" color="textSecondary">Carpeta</Typography>
                <Typography variant="body2">{detail.folder_name || '—'}</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography variant="caption" color="textSecondary">Inicio</Typography>
                <Typography variant="body2">{fmtDate(detail.started_at)}</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography variant="caption" color="textSecondary">Término</Typography>
                <Typography variant="body2">{fmtDate(detail.finished_at)}</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography variant="caption" color="textSecondary">Duración</Typography>
                <Typography variant="body2">{fmtDuration(detail.duration_seconds)}</Typography></Grid>
            </Grid>

            {isRunning ? (
              <>
                <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mb: 1 }}>
                  Agente trabajando en segundo plano vía opencode-tor…
                  <LinearProgress sx={{ mt: 1 }} />
                </Alert>
                <Paper variant="outlined" ref={logRef}
                  sx={{ p: 2, maxHeight: 380, overflow: 'auto', bgcolor: '#101418' }}>
                  <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.78rem', color: '#9fe8a8', fontFamily: 'monospace' }}>
                    {detail.logs || 'Esperando salida del agente…'}
                  </pre>
                </Paper>
              </>
            ) : (
              <>
                {detail.status === 'failed' && (
                  <Alert severity="error" sx={{ mb: 1 }}>{detail.error || 'La ejecución falló.'}</Alert>
                )}
                <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 1 }}>
                  <Tab label="Resultado" />
                  <Tab label="Verbosidad del agente" />
                  <Tab icon={<FolderOpen />} iconPosition="start"
                    label={`Documentos (${(detail.files || []).length})`} />
                </Tabs>
                {tab === 0 && (
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                    <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, fontSize: '0.85rem' }}>
                      {detail.result || '(sin resultado)'}
                    </pre>
                  </Paper>
                )}
                {tab === 1 && (
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto', bgcolor: '#101418' }}>
                    <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.75rem', color: '#9fe8a8', fontFamily: 'monospace' }}>
                      {detail.logs || '(sin verbosidad registrada para esta ejecución)'}
                    </pre>
                  </Paper>
                )}
                {tab === 2 && (
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Documento</strong></TableCell>
                          <TableCell><strong>Tamaño</strong></TableCell>
                          <TableCell align="right"><strong>Acción</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(detail.files || []).map((f) => (
                          <TableRow key={f.id}>
                            <TableCell>{f.original_name || f.name}</TableCell>
                            <TableCell>{fmtSize(f.file_size)}</TableCell>
                            <TableCell align="right">
                              <Button size="small" variant="outlined" href={f.url} target="_blank">Ver</Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {(detail.files || []).length === 0 && (
                          <TableRow><TableCell colSpan={3}>Sin documentos en la carpeta.</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------- Página principal ----------
export default function TareasProgramadasPage() {
  const [mainTab, setMainTab] = useState(0);
  const [tasks, setTasks] = useState([]);
  const [folders, setFolders] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // historial - filtros
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');

  // calendario
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  // creación
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    folder_id: '', criterion: '', schedule_type: 'once',
    run_at: '', run_time: '09:00', weekday: 0,
  });

  // detalle ejecución
  const [detailRunId, setDetailRunId] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, fRes, cRes] = await Promise.all([
        api.get('/tareas-programadas'),
        api.get('/instrucciones/folders'),
        api.get('/tareas-programadas/criteria'),
      ]);
      setTasks(tRes.data.items || []);
      setFolders(fRes.data.items || []);
      setCriteria(cRes.data.items || []);
      setError(null);
    } catch (err) {
      setError('Error cargando tareas: ' + (err.response?.data?.detail || err.message));
    }
    setLoading(false);
  }, []);

  const loadRuns = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await api.get('/tareas-programadas/runs/all', { params });
      setRuns(res.data.items || []);
    } catch (err) { /* silencioso */ }
  }, [statusFilter]);

  useEffect(() => { loadAll(); loadRuns(); }, [loadAll, loadRuns]);
  useEffect(() => {
    const iv = setInterval(() => { loadRuns(); }, 10000);
    return () => clearInterval(iv);
  }, [loadRuns]);

  const createTask = async () => {
    try {
      const payload = {
        folder_id: form.folder_id,
        criterion: form.criterion,
        schedule_type: form.schedule_type,
        run_time: form.run_time,
        weekday: form.weekday,
      };
      if (form.schedule_type === 'once') {
        if (!form.run_at) { alert('Selecciona fecha y hora de ejecución'); return; }
        payload.run_at = new Date(form.run_at).toISOString();
      }
      await api.post('/tareas-programadas', payload);
      setCreateOpen(false);
      loadAll();
    } catch (err) {
      alert('Error creando tarea: ' + (err.response?.data?.detail || err.message));
    }
  };

  const toggleStatus = async (task) => {
    try {
      await api.patch(`/tareas-programadas/${task.id}`, {
        status: task.status === 'active' ? 'paused' : 'active',
      });
      loadAll();
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  const deleteTask = async (task) => {
    if (!confirm(`¿Eliminar la tarea "${task.criterion_title}" de ${task.folder_name}?`)) return;
    try {
      await api.delete(`/tareas-programadas/${task.id}`);
      loadAll(); loadRuns();
    } catch (err) { alert('Error eliminando: ' + (err.response?.data?.detail || err.message)); }
  };

  const runDevelopment = async (task) => {
    try {
      setBusyId(task.id);
      await api.post(`/tareas-programadas/${task.id}/develop`);
      await loadAll();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo disparar el desarrollo');
    } finally {
      setBusyId(null);
    }
  };

    const runNow = async (task) => {
    try {
      await api.post(`/tareas-programadas/${task.id}/run`);
      setTimeout(() => { loadRuns(); setMainTab(1); }, 1200);
    } catch (err) { alert('Error ejecutando: ' + (err.response?.data?.detail || err.message)); }
  };

  const openLatestRun = async (task) => {
    setMainTab(1);
    try {
      const res = await api.get(`/tareas-programadas/${task.id}/runs?limit=1`);
      if ((res.data.items || []).length > 0) setDetailRunId(res.data.items[0].id);
    } catch (err) { /* noop */ }
  };

  // ---- calendario ----
  const cells = [];
  const firstWeekday = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(viewYear, viewMonth, d));

  const monthOccurrences = tasks.flatMap(t => occurrencesInMonth(t, viewYear, viewMonth));
  const byDay = {};
  for (const o of monthOccurrences) {
    const k = o.date.getDate();
    (byDay[k] = byDay[k] || []).push(o);
  }

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(viewYear - 1); setViewMonth(11); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(viewYear + 1); setViewMonth(0); }
    else setViewMonth(viewMonth + 1);
  };
  const goToday = () => { setViewYear(today.getFullYear()); setViewMonth(today.getMonth()); };

  const statusChipColor = (s) =>
    s === 'active' ? 'success' : s === 'completed' ? 'info' : s === 'running' ? 'warning' : 'default';

  const filteredRuns = runs.filter((r) => {
    if (!searchText.trim()) return true;
    const q = searchText.toLowerCase();
    return (r.criterion_title || '').toLowerCase().includes(q)
      || (r.folder_name || '').toLowerCase().includes(q);
  });

  const runningCount = runs.filter((r) => r.status === 'running').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Tareas Programadas</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
          Nueva Programación
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs value={mainTab} onChange={(e, v) => setMainTab(v)} sx={{ mb: 2 }}>
        <Tab icon={<ScheduleIcon />} iconPosition="start" label="Programación" />
        <Tab icon={<History />}
          label={`Historial${runningCount > 0 ? ` (${runningCount} activas)` : ''}`} />
      </Tabs>

      {/* ================= TAB PROGRAMACIÓN ================= */}
      {mainTab === 0 && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <IconButton onClick={prevMonth}><ChevronLeft /></IconButton>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon color="primary" />
                  <Typography variant="h6">{MONTH_LABELS[viewMonth]} {viewYear}</Typography>
                  <IconButton size="small" onClick={goToday} title="Hoy"><Today /></IconButton>
                </Box>
                <IconButton onClick={nextMonth}><ChevronRight /></IconButton>
              </Box>

              <Grid container spacing={0} sx={{ borderTop: '1px solid #e0e0e0', borderLeft: '1px solid #e0e0e0' }}>
                {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((d) => (
                  <Grid item xs={12 / 7} key={d}
                    sx={{ borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', p: 0.5 }}>
                    <Typography variant="caption" fontWeight="bold" align="center" display="block">{d}</Typography>
                  </Grid>
                ))}
                {cells.map((date, idx) => {
                  const dayNum = date ? date.getDate() : null;
                  const isToday = date && date.toDateString() === today.toDateString();
                  const items = dayNum ? (byDay[dayNum] || []) : [];
                  return (
                    <Grid item xs={12 / 7} key={idx}
                      sx={{
                        borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0',
                        minHeight: 72, p: 0.5,
                        bgcolor: isToday ? 'action.selected' : 'transparent',
                      }}>
                      {dayNum && (
                        <>
                          <Typography variant="caption" color={isToday ? 'primary' : 'textSecondary'}>{dayNum}</Typography>
                          {items.slice(0, 2).map((o, i) => (
                            <Chip key={i}
                              label={`${String(o.date.getHours()).padStart(2, '0')}:${String(o.date.getMinutes()).padStart(2, '0')} ${o.task.criterion_title.split(' ')[0]}`}
                              size="small"
                              color={o.task.status === 'active' ? 'primary' : 'default'}
                              variant="outlined"
                              onClick={() => openLatestRun(o.task)}
                              sx={{ display: 'flex', mt: 0.25, fontSize: '0.65rem', height: 20, cursor: 'pointer' }}
                            />
                          ))}
                          {items.length > 2 && (
                            <Typography variant="caption" color="textSecondary">+{items.length - 2} más</Typography>
                          )}
                        </>
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Tareas Agendadas</Typography>
              {loading && tasks.length === 0 ? (
                <CircularProgress size={24} />
              ) : tasks.length === 0 ? (
                <Typography color="textSecondary">No hay tareas programadas. Crea una con "Nueva Programación".</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Criterio</strong></TableCell>
                        <TableCell><strong>Carpeta</strong></TableCell>
                        <TableCell><strong>Agenda</strong></TableCell>
                        <TableCell><strong>Próxima ejecución</strong></TableCell>
                        <TableCell><strong>Última</strong></TableCell>
                        <TableCell><strong>Estado</strong></TableCell>
                        <TableCell align="right"><strong>Acciones</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tasks.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell>{t.criterion_title}</TableCell>
                          <TableCell>{t.folder_name}</TableCell>
                          <TableCell>
                            {t.schedule_type === 'once' && 'Única'}
                            {t.schedule_type === 'daily' && `Diaria ${t.run_time}`}
                            {t.schedule_type === 'weekly' && `Semanal (${WEEKDAY_LABELS[t.weekday ?? 0]} ${t.run_time})`}
                          </TableCell>
                          <TableCell>{fmtDate(t.next_run_at)}</TableCell>
                          <TableCell>
                            {fmtDate(t.last_run_at)}
                            {t.last_run_status && (
                              <Chip label={STATUS_META[t.last_run_status]?.label || t.last_run_status}
                                size="small" clickable
                                color={STATUS_META[t.last_run_status]?.color || 'default'}
                                sx={{ ml: 1 }}
                                onClick={() => openLatestRun(t)} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip label={t.status} size="small" color={statusChipColor(t.status)} />
                            {(() => {
                              const dm = devStateMeta(t);
                              return dm ? (
                                <Box sx={{ mt: 0.5 }}>
                                  <Chip size="small" color={dm.color} icon={<Code />}
                                    label={dm.label}
                                    title={dm.label === 'Completada 100%'
                                      ? 'Desarrollo ejecutado'
                                      : 'Las bases exigen desarrollo/POC pendiente'} />
                                </Box>
                              ) : null;
                            })()}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" title="Ejecutar ahora" onClick={() => runNow(t)} disabled={t.status === 'completed'}><PlayArrow /></IconButton>
                            {t.requires_dev && !t.dev_completed && (
                              <Button size="small" variant="contained" color="warning"
                                startIcon={<PlayArrow />} disabled={busyId === t.id}
                                onClick={() => runDevelopment(t)}
                                sx={{ mr: 0.5, whiteSpace: 'nowrap' }}>
                                ▶ Ejecutar Desarrollo
                              </Button>
                            )}
                            <IconButton size="small" title={t.status === 'active' ? 'Pausar' : 'Reanudar'} onClick={() => toggleStatus(t)}>
                              {t.status === 'active' ? <Pause /> : <PlayCircleOutline />}
                            </IconButton>
                            <IconButton size="small" title="Ver última ejecución" onClick={() => openLatestRun(t)} disabled={!t.last_run_at}><Visibility /></IconButton>
                            <IconButton size="small" title="Eliminar" onClick={() => deleteTask(t)}><Delete /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ================= TAB HISTORIAL ================= */}
      {mainTab === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Filtrar por estado</InputLabel>
                <Select value={statusFilter} label="Filtrar por estado"
                  onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="">Todos los estados</MenuItem>
                  <MenuItem value="running">En progreso</MenuItem>
                  <MenuItem value="completed">Completadas</MenuItem>
                  <MenuItem value="failed">Fallidas</MenuItem>
                </Select>
              </FormControl>
              <TextField size="small" placeholder="Buscar por carpeta o criterio…"
                value={searchText} onChange={(e) => setSearchText(e.target.value)}
                sx={{ minWidth: 260 }} />
              {runningCount > 0 && (
                <Chip color="warning" icon={<ScheduleIcon />}
                  label={`${runningCount} ejecución(es) en progreso`} />
              )}
            </Box>

            {filteredRuns.length === 0 ? (
              <Typography color="textSecondary">No hay ejecuciones que coincidan con el filtro.</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Inicio</strong></TableCell>
                      <TableCell><strong>Criterio</strong></TableCell>
                      <TableCell><strong>Carpeta</strong></TableCell>
                      <TableCell><strong>Duración</strong></TableCell>
                      <TableCell><strong>Estado</strong></TableCell>
                      <TableCell align="right"><strong>Detalle</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRuns.map((r) => (
                      <TableRow key={r.id} hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setDetailRunId(r.id)}>
                        <TableCell>{fmtDate(r.started_at)}</TableCell>
                        <TableCell>{r.criterion_title}</TableCell>
                        <TableCell>{r.folder_name}</TableCell>
                        <TableCell>{fmtDuration(r.duration_seconds)}</TableCell>
                        <TableCell>
                          <Chip clickable size="small"
                            label={STATUS_META[r.status]?.label || r.status}
                            color={STATUS_META[r.status]?.color || 'default'} />
                          {(() => {
                            const dm = devStateMeta(r);
                            return dm ? (
                              <Chip size="small" sx={{ ml: 0.5 }} color={dm.color} icon={<Code />}
                                label={dm.label} />
                            ) : null;
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" title="Ver detalle"
                            onClick={(e) => { e.stopPropagation(); setDetailRunId(r.id); }}>
                            <Visibility />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* DIALOG CREAR */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva Tarea Programada</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Carpeta de licitación</InputLabel>
            <Select value={form.folder_id} label="Carpeta de licitación"
              onChange={(e) => setForm({ ...form, folder_id: e.target.value })}>
              {folders.map((f) => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Criterio de análisis</InputLabel>
            <Select value={form.criterion} label="Criterio de análisis"
              onChange={(e) => setForm({ ...form, criterion: e.target.value })}>
              {criteria.map((c) => <MenuItem key={c.key} value={c.key}>{c.title}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Tipo de agenda</InputLabel>
            <Select value={form.schedule_type} label="Tipo de agenda"
              onChange={(e) => setForm({ ...form, schedule_type: e.target.value })}>
              <MenuItem value="once">Ejecución única</MenuItem>
              <MenuItem value="daily">Diaria</MenuItem>
              <MenuItem value="weekly">Semanal</MenuItem>
            </Select>
          </FormControl>

          {form.schedule_type === 'once' && (
            <TextField type="datetime-local" label="Fecha y hora de ejecución"
              value={form.run_at} InputLabelProps={{ shrink: true }}
              onChange={(e) => setForm({ ...form, run_at: e.target.value })} />
          )}

          {form.schedule_type === 'weekly' && (
            <FormControl fullWidth>
              <InputLabel>Día de la semana</InputLabel>
              <Select value={form.weekday} label="Día de la semana"
                onChange={(e) => setForm({ ...form, weekday: e.target.value })}>
                {WEEKDAY_LABELS.map((d, i) => <MenuItem key={i} value={i}>{d}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          {(form.schedule_type === 'daily' || form.schedule_type === 'weekly') && (
            <TextField type="time" label="Hora de ejecución" value={form.run_time}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setForm({ ...form, run_time: e.target.value })} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={createTask}
            disabled={!form.folder_id || !form.criterion}>
            Programar
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG DETALLE EJECUCIÓN */}
      <RunDetailDialog runId={detailRunId} open={!!detailRunId} onClose={() => setDetailRunId(null)} />
    </Box>
  );
}
